"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  SparklesIcon, 
  UsersIcon, 
  ClockIcon, 
  RocketLaunchIcon,
  HeartIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function About() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animations simplifiées pour mobile
  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromLeft = {
    hidden: { opacity: 0, x: isMobile ? -20 : -50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const slideInFromRight = {
    hidden: { opacity: 0, x: isMobile ? 20 : 50 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: { duration: isMobile ? 0.4 : 0.7, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const features = [
    {
      icon: ClockIcon,
      title: "24/7 Disponible",
      description: "Réponses instantanées à toute heure, pour une assistance continue de vos collaborateurs.",
      gradient: "from-blue-500 to-cyan-500",
      bgGradient: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      hoverBorder: "hover:border-blue-400",
      animation: slideInFromLeft
    },
    {
      icon: UsersIcon,
      title: "Pour tous les collaborateurs",
      description: "De l'onboarding des nouveaux arrivants aux questions quotidiennes des équipes.",
      gradient: "from-purple-500 to-pink-500",
      bgGradient: "from-purple-50 to-pink-50",
      borderColor: "border-purple-200",
      hoverBorder: "hover:border-purple-400",
      animation: scaleIn
    },
    {
      icon: SparklesIcon,
      title: "IA Spécialisée",
      description: "Technologie avancée adaptée spécifiquement aux besoins des ressources humaines.",
      gradient: "from-emerald-500 to-teal-500",
      bgGradient: "from-emerald-50 to-teal-50",
      borderColor: "border-emerald-200",
      hoverBorder: "hover:border-emerald-400",
      animation: slideInFromRight
    }
  ];

  const stats = [
    {
      icon: RocketLaunchIcon,
      number: "85%",
      label: "Réduction du temps de traitement",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: HeartIcon,
      number: "92%",
      label: "Satisfaction collaborateurs",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      icon: LightBulbIcon,
      number: "60%",
      label: "Temps libéré pour les équipes RH",
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  return (
    <section id="about" className={`py-16 bg-gradient-to-br from-white via-violet-50/20 to-purple-50/20 relative overflow-hidden smooth-scroll-section ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Badge section */}
          <motion.div
            variants={scaleIn}
            whileHover={isMobile ? {} : { scale: 1.05 }}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-violet-50 to-purple-50 border-2 border-violet-200 rounded-full px-6 py-3 mb-8 shadow-lg backdrop-blur-sm"
          >
            {!isMobile ? (
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-6 h-6 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center"
              >
                <SparklesIcon className="w-4 h-4 text-white" />
              </motion.div>
            ) : (
              <div className="w-6 h-6 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              À propos de ConnectRH
            </span>
          </motion.div>

          {/* Titre principal */}
          <motion.h2
            variants={fadeInUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-relaxed py-4"
            style={{ lineHeight: '1.3' }}
          >
            {!isMobile ? (
              <motion.span
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity, 
                  ease: "linear" 
                }}
                className="inline-block bg-gradient-to-r from-gray-900 via-violet-600 to-gray-900 bg-[length:200%_100%] bg-clip-text text-transparent"
              >
                Votre assistant intelligent
              </motion.span>
            ) : (
              <span className="inline-block bg-gradient-to-r from-gray-900 to-violet-600 bg-clip-text text-transparent">
                Votre assistant intelligent
              </span>
            )}
            <span className="block bg-gradient-to-r from-violet-600 to-purple-600 gradient-text-safe mt-2">
              pour l'accompagnement RH
            </span>
          </motion.h2>

          {/* Description principale avec effet de machine à écrire */}
          <motion.div
            variants={fadeInUp}
            className="max-w-4xl mx-auto mb-16"
          >
            <p className="text-xl text-gray-600 leading-relaxed">
              ConnectRH est un assistant intelligent conçu pour{" "}
              <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                accompagner les collaborateurs
              </span>{" "}
              à chaque étape de leur parcours, en répondant instantanément à leurs questions RH. 
              Il améliore l'efficacité des équipes RH et assure une expérience fluide aux nouveaux arrivants.
            </p>
          </motion.div>

          {/* Statistiques animées */}
          <motion.div
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-20"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ 
                  scale: 1.05,
                  transition: { duration: 0.2 }
                }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-gradient-to-r ${stat.gradient} rounded-xl flex items-center justify-center mb-4 mx-auto`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className={`text-3xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-2`}
                >
                  {stat.number}
                </motion.div>
                <p className="text-sm text-gray-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Features principales */}
          <motion.div
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={feature.animation}
                whileHover={isMobile ? {} : { 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className={`
                  bg-gradient-to-br ${feature.bgGradient} rounded-3xl p-8 border-2 ${feature.borderColor} ${feature.hoverBorder}
                  transition-all duration-300 hover:shadow-2xl relative overflow-hidden group
                `}
              >
                {/* Effet de brillance au survol - désactivé sur mobile */}
                {!isMobile && (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                )}
                
                {/* Icône animée */}
                {!isMobile ? (
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                    className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg`}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </motion.div>
                ) : (
                  <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-lg`}>
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                )}

                {/* Contenu */}
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-center">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
} 